import * as R from 'ramda';

import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { REQUIRED_ERROR } from 'CorePackage/MainModule/constants/validationErrors';
import { composeValidators } from 'CorePackage/MainModule/validators/composeValidators';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { makeNoLaterThenValidator } from 'CorePackage/MainModule/validators/isNoLaterThen';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { getDocumentsValidate } from 'KnmErknmPackage/MainModule/common/getDocumentsValidate';
import { getInspectorsErknmValidate } from 'KnmErknmPackage/MainModule/common/getInspectorsErknmValidate';
import { getObjectsErknmValidate } from 'KnmErknmPackage/MainModule/common/getObjectsErknmValidate';
import {
  getViolatedMandatoryRequirementsValidator,
  makeRequiredArray,
} from 'KnmErknmPackage/MainModule/common/violatedMandatoryRequirementsValidator';
import { FormErrors } from 'KnmPackage/MainModule/types/FormErrors';
import { getErknmAgreementValidate } from 'KnmErknmPackage/MainModule/common/getErknmAgreementValidate';
import { IErknmFormAgreement } from 'KnmErknmPackage/MainModule/interfaces/IErknmFormAgreement';
import { KnmReasonTypeNameEnum } from 'KnmPackage/MainModule/enums/KnmReasonTypeNameEnum';

import { IDecisionForm } from '../interfaces/IDecisionForm';
import { DecisionFormValidator } from '../types/DecisionFormValidator';
import { IKnmErknmFormEvent } from '../../../KnmErknmPackage/MainModule/interfaces/IKnmErknmFormEvent';
import { makeNoSoonerThenValidator } from '../../../CorePackage/MainModule/validators/isNoSoonerThan';
import { getSubmittedDocumentsValidate } from '../../../KnmErknmPackage/MainModule/common/getSubmittedDocumentsValidate';
import {
  getDecisionEventTypesSelectOptions,
  getDecisionIsPrescriptionFailed,
  getDecisionIsPrescriptionOldRules,
  getIsDecisionReasonValidate,
} from '../selectors/decisionSelectors';
import { store } from '../../../CorePackage/MainModule/common/store';
import { getJudgmentsValidate } from '../../../KnmErknmPackage/MainModule/common/getJudmentsValidate';
import { getDecisionReasonsValidate } from './getDecisionReasonsValidate';
import { getEventUavValidator } from '../../../KnmErknmPackage/MainModule/common/getEventUavValidate';
import { getDecisionOrganizationValidate } from './getDecisionOrganizationValidate';
import { getIsDecisionViolationsInBody } from './getIsDecisionViolationsInBody';
import { cloneFormErrors } from '../../../CorePackage/MainModule/common/cloneFormErrors';

const decisionValidator = ({
  values,
  isNewDecision,
  isSupervisor,
  isWatchOnly,
  isDecisionStatusFillInProcess,
  isDecisionStatusReadyForApprove,
  isEditableInStatusCommentsExist,
  isDecisionStatusCompleted,
  settings,
}: DecisionFormValidator & { values: IDecisionForm }): FormErrors<IDecisionForm> => {
  const {
    status,
    knoOrganization,
    kind,
    kindControl,
    basisDocumentDateTime,
    basisDocumentNumber,
    organization,
    objects,
    prosecutorOrganization,
    inspectors,
    documents,
    created,
    judgments,
    events,
    reasons,
    agreements,
    isUavUsed,
    uavTechnologies,
  } = values;

  const state = store.getState();

  const isIntegrationCategoryValidate = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_4_1_55_STARTS_FROM,
  );
  const isUse4151 = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_223_4_1_16_STARTS_FROM,
  );
  const isUse5234153 = getIsDecisionReasonValidate(state);

  const isUse4123 = isLaterThanAdminSetting(created, settings, AdminSettingCodesEnum.KNM_VALIDATION_4_1_23_STARTS_FROM);
  const isUse2234114 = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_223_4_1_14_STARTS_FROM,
  );

  const isEnablePrescriptionCheck = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_105_166_25_4_1_30_STARTS_FROM,
  );

  const getEventsErknmValidate = (): FormErrors<IKnmErknmFormEvent>[] | string | undefined => {
    const eventTypeOptions = getDecisionEventTypesSelectOptions(state);

    if (!eventTypeOptions?.length || isDecisionStatusCompleted) {
      return undefined;
    }

    if (!events?.length) {
      return makeRequired('Поле не может быть пустым')(events);
    }

    return events.map(item => ({
      ...getEventValidate(item),
    }));
  };

  const getEventValidate = (item: IKnmErknmFormEvent): FormErrors<IKnmErknmFormEvent> => {
    const isWithoutAction = item.type?.label === 'Осуществляется без действия';

    return {
      type: makeRequired('Поле не может быть пустым')(item.type),
      interactionCategory: makeRequired('Поле не может быть пустым')(item.interactionCategory),
      startDate: composeValidators(
        makeNoLaterThenValidator('Дата начала не должна быть позже даты окончания', () => item.stopDate),
        makeRequired('Поле не может быть пустым', () => isWithoutAction),
      )(item.startDate),
      stopDate: composeValidators(
        makeNoSoonerThenValidator('Дата окончания не должна быть раньше даты начала', () => item.startDate),
        makeRequired('Поле не может быть пустым', () => isWithoutAction),
      )(item.stopDate),
      submittedDocuments: getSubmittedDocumentsValidate(item.submittedDocuments, item.type),
    };
  };

  // Поля повторного нарушения можно требовать только там, где интерфейс их показывает. Иначе на
  // паспорте, где исходное КНМ создано по старым правилам, валидация просит поле,
  // которого на форме нет.
  const isRepeatValidationChecking =
    (reasons?.[0].reason?.type?.name === KnmReasonTypeNameEnum.PRESCRIPTION_CHECK ||
      getDecisionIsPrescriptionFailed(state) === true) &&
    getDecisionIsPrescriptionOldRules(state) === false;

  // Нарушения на уровне тела паспорта валидируем только если они там отображаются.
  const isViolationsInBody = getIsDecisionViolationsInBody({
    created,
    settings,
    isNeedToAddAct: values.isNeedToAddAct,
    act: organization?.act,
  });

  const resultValidateObj: FormErrors<IDecisionForm> = {
    knoOrganization: makeRequired(REQUIRED_ERROR)(knoOrganization?.value),
    kindControl: makeRequired(REQUIRED_ERROR)(kindControl?.value),
    kind: makeRequired(REQUIRED_ERROR)(kind?.value),
    basisDocumentDateTime: isUse5234153 ? undefined : makeRequired(REQUIRED_ERROR)(basisDocumentDateTime),
    basisDocumentNumber: isUse5234153 ? undefined : makeRequired(REQUIRED_ERROR)(basisDocumentNumber),
    organization: organization
      ? getDecisionOrganizationValidate({
          item: organization,
          settings,
          state,
          values,
          isDecisionStatusFillInProcess,
          isDecisionStatusReadyForApprove,
          isNewDecision,
          isSupervisor,
          isWatchOnly,
        })
      : undefined,
    objects:
      objects && objects.length
        ? getObjectsErknmValidate(objects, isEditableInStatusCommentsExist, undefined, isUse4123, isUse2234114)
        : undefined,
    events: getEventsErknmValidate(),
    prosecutorOrganization: makeRequired('Поле не может быть пустым')(prosecutorOrganization),
    inspectors: getInspectorsErknmValidate(inspectors, isEditableInStatusCommentsExist),
    isViolatedMandatoryRequirements: status
      ? makeRequired('Поле не может быть пустым')(values.isViolatedMandatoryRequirements)
      : undefined,
    isNeedToAddAct: created && isUse4151 ? makeRequired('Поле не может быть пустым')(values.isNeedToAddAct) : undefined,
    violatedMandatoryRequirements:
      values.isViolatedMandatoryRequirements?.value === true && isViolationsInBody
        ? composeValidators(
            makeRequiredArray('Раздел не может быть пустым'),
            getViolatedMandatoryRequirementsValidator({
              isSigned: undefined,
              isValidateRequiredViolatedMandatory: true,
              isDecisionMode: false,
              isProdReady: true,
              isRepeatValidationChecking:
                isEnablePrescriptionCheck && isRepeatValidationChecking && !isDecisionStatusFillInProcess,
              inDecisionBody: true,
              isNeedViolationPLanFixValidate: true,
              isDecision: true,
              isActMode: false,
              isViolationEventsRequired: false,
            }),
          )(values.violatedMandatoryRequirements)
        : undefined,

    documents: getDocumentsValidate(documents, isIntegrationCategoryValidate),
    judgments: getJudgmentsValidate(judgments),
    reasons: isUse5234153 ? getDecisionReasonsValidate(reasons) : undefined,
    agreements: agreements?.map(agreement =>
      getErknmAgreementValidate({
        agreement,
        isPaperless: !!organization?.resultDecisions?.find(i => i.guid === agreement?.guid)?.paperless,
        isSupervisor: !!isSupervisor,
      }),
    ),
    uavTechnologies: isUavUsed === true ? getEventUavValidator(uavTechnologies) : undefined,
  };

  const resultValidate = cloneFormErrors(resultValidateObj);

  if (resultValidate['agreements' as keyof IDecisionForm]) {
    resultValidate['agreements' as keyof IDecisionForm].forEach((agreement: IErknmFormAgreement, index: number) => {
      if (Array.isArray(agreement?.terminate?.terminationReasons)) {
        let isReasonsEmpty = true;

        agreement?.terminate?.terminationReasons?.forEach(reason => {
          if (!R.isNil(reason)) {
            isReasonsEmpty = false;
          }
        });

        if (agreement?.terminate && isReasonsEmpty) {
          resultValidate['agreements' as keyof IDecisionForm][index].terminate.terminationReasons = undefined;
        }
      }

      if (Array.isArray(agreement?.changes)) {
        agreement?.changes?.forEach((change, changeIndex) => {
          let isReasonsEmpty = true;

          change.types?.forEach(type => {
            if (!R.isNil(type)) {
              isReasonsEmpty = false;
            }
          });

          if (isReasonsEmpty) {
            resultValidate['agreements' as keyof IDecisionForm][index].changes[changeIndex].types = undefined;
          }

          isReasonsEmpty = true;
          change.reasons?.forEach(reason => {
            if (!R.isNil(reason)) {
              isReasonsEmpty = false;
            }
          });

          if (isReasonsEmpty) {
            resultValidate['agreements' as keyof IDecisionForm][index].changes[changeIndex].reasons = undefined;
          }
        });
      }
    });
  }

  return resultValidate;
};

export function decisionFormValidator(props: DecisionFormValidator) {
  return (values: IDecisionForm) => {
    return decisionValidator({ values, ...props });
  };
}
